import type { DashboardState } from "../types";
import { MockApi } from "./mock";

/**
 * The dashboard talks ONLY to this interface. In production it's backed by the
 * Cloudflare Worker brain (which holds the HA token server-side and reads D1);
 * in dev / demo it's backed by an in-memory simulation.
 */
export interface IrrigationApi {
  getState(): Promise<DashboardState>;
  runZone(id: string, minutes: number): Promise<void>;
  stopZone(id: string): Promise<void>;
  setDuration(id: string, minutes: number): Promise<void>;
  setEnabled(id: string, enabled: boolean): Promise<void>;
  setRainDelay(paused: boolean): Promise<void>;
  sync(): Promise<void>;
}

class HttpApi implements IrrigationApi {
  constructor(private base: string) {}

  private async post(path: string, body?: unknown) {
    const res = await fetch(`${this.base}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include", // Cloudflare Access cookie
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`${path} → ${res.status}`);
  }

  async getState(): Promise<DashboardState> {
    const res = await fetch(`${this.base}/api/state`, { credentials: "include" });
    if (!res.ok) throw new Error(`/api/state → ${res.status}`);
    return res.json();
  }
  runZone(id: string, minutes: number) {
    return this.post(`/api/zone/${id}/run`, { minutes });
  }
  stopZone(id: string) {
    return this.post(`/api/zone/${id}/stop`);
  }
  setDuration(id: string, minutes: number) {
    return this.post(`/api/zone/${id}/duration`, { minutes });
  }
  setEnabled(id: string, enabled: boolean) {
    return this.post(`/api/zone/${id}/enable`, { enabled });
  }
  setRainDelay(paused: boolean) {
    return this.post(`/api/rain-delay`, { paused });
  }
  sync() {
    return this.post(`/api/sync`);
  }
}

/**
 * VITE_API_BASE unset or "mock" → in-memory demo. Set it to the Worker origin
 * (e.g. "" for same-origin on Cloudflare Pages, or "http://localhost:8787" in
 * dev) to drive the real controller.
 */
const API_BASE = import.meta.env.VITE_API_BASE as string | undefined;

/** True when no Worker origin is configured, so the whole app runs on in-memory
 *  mock data (not the live hybrid). Surfaced as a "demo data" banner in the UI. */
export const IS_MOCK = API_BASE === undefined || API_BASE === "mock";

export function createApi(): IrrigationApi {
  return IS_MOCK ? new MockApi() : new HttpApi(API_BASE as string);
}
